/**
 * Add somebody to the roster, or edit who they are and what they do.
 *
 * One component for both, because the two forms differ by exactly one field. A
 * separate AddPerson would duplicate the specialisation picker - the fiddliest part
 * of this file - and the two copies would drift the first time the vocabulary grows.
 *
 * THREE THINGS HERE ARE NOT OBVIOUS.
 *
 * 1. Email is the primary key and is therefore only editable on create. Changing it
 *    afterwards is not a rename, it is a new row plus an orphan, and every project
 *    that pointed at the old address would silently become unowned. Correcting a
 *    typo means deactivating the wrong row and adding the right one.
 *
 * 2. Scalar fields follow the usual dirtyFields rule - only what changed is sent -
 *    but `specialisations` cannot, because it REPLACES rather than merges. React
 *    Hook Form would mark the whole object dirty the moment one skill moved, and
 *    sending a partially-dirty map would drop the untouched ones. So the list is
 *    rebuilt from the form and compared with what was there; it is sent only if the
 *    resulting SET differs. See the note in types.ts and fast/app/schemas/people.py.
 *
 * 3. Deactivation, not deletion. The API has no hard delete and this form does not
 *    pretend otherwise: their assignments keep pointing at them, because erasing who
 *    was responsible for a lane is a worse outcome than a greyed-out name.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { createPerson, deactivatePerson, describeError, patchPerson } from '../services/api';
import { palette, radius } from '../styles/theme';
import {
  ErrorText,
  Hint,
  Input,
  Label,
  PrimaryButton,
  SecondaryButton,
  Select,
  VisuallyHidden,
} from '../styles/ui';
import type { Person, PersonPatch, SkillInfo, SkillLevel, Specialisation } from '../types';

/** The three states a skill can be in on this form. `none` is not a stored value. */
type Choice = 'none' | SkillLevel;

interface FormValues {
  email: string;
  name: string;
  manager_email: string;
  active: boolean;
  /** skill value -> choice. Every skill in the vocabulary is present, most as 'none'. */
  levels: Record<string, Choice>;
}

const CHOICES: { value: Choice; label: string; hint: string }[] = [
  { value: 'none', label: 'No', hint: 'Not one of their areas' },
  { value: 'secondary', label: 'Could cover', hint: 'Can pick it up if needed' },
  { value: 'primary', label: 'Ask them', hint: 'The obvious person for this' },
];

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(200px, 1.2fr) minmax(200px, 1.2fr) minmax(160px, 1fr);
  gap: 12px 14px;
  align-items: start;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid ${palette.border};
  padding-top: 12px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${palette.ink};
  cursor: pointer;
`;

/*
  A fieldset rather than a div, and a legend rather than a heading, so that a screen
  reader announces "Specialisations" before each skill's radio group. Ten groups of
  three otherwise arrive as thirty unexplained radios.
*/
const Skills = styled.fieldset`
  grid-column: 1 / -1;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid ${palette.border};
  border-radius: ${radius.md};
  background: ${palette.blush};
`;

const SkillsLegend = styled.legend`
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${palette.inkSoft};
`;

const SkillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 6px 18px;
`;

const SkillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 30px;
`;

const SkillName = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: ${palette.ink};
`;

/*
  A segmented control built from real radios: the input is visually hidden and the
  label is the button. Keyboard behaviour - arrow keys moving within the group,
  tab skipping past it - comes free from the radio group, which a set of
  aria-pressed buttons would have had to reimplement by hand.
*/
const Segments = styled.div`
  display: inline-flex;
  border: 1px solid ${palette.border};
  border-radius: ${radius.pill};
  background: ${palette.card};
  overflow: hidden;
`;

const Segment = styled.label<{ $on: boolean; $level: Choice }>`
  position: relative;
  padding: 3px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  /*
    Three distinct fills, not two. An earlier version painted every selected segment
    the same grey, so "No" and "Could cover" were indistinguishable unless you worked
    out which of the three cells was the filled one - on a ten-row grid that is
    unreadable. Grey / bubblegum / hot pink is a strength ramp that differs in
    lightness as well as hue, so it survives the colour vision deficiencies the
    palette header is written around, and the selected word is still there to read.
  */
  color: ${(p) => {
    if (!p.$on) {
      return palette.inkSoft;
    }
    if (p.$level === 'primary') {
      return '#ffffff';
    }
    return p.$level === 'secondary' ? '#5C1138' : palette.ink;
  }};
  background: ${(p) => {
    if (!p.$on) {
      return 'transparent';
    }
    if (p.$level === 'primary') {
      return palette.hotPink;
    }
    return p.$level === 'secondary' ? palette.bubblegum : palette.slate;
  }};
  transition: background-color 120ms ease, color 120ms ease;

  &:hover {
    background: ${(p) => (p.$on ? undefined : palette.blush)};
  }

  /* The ring goes on the label, because the input it belongs to is not on screen. */
  &:has(input:focus-visible) {
    outline: 2px solid ${palette.hotPink};
    outline-offset: -2px;
  }
`;

/** The stored list, rebuilt from the form. Sorted so two lists compare by value. */
export function buildSpecialisations(levels: Record<string, Choice>): Specialisation[] {
  return Object.entries(levels)
    .filter((entry): entry is [string, SkillLevel] => entry[1] !== 'none')
    .map(([skill, level]) => ({ skill, level }))
    .sort((a, b) => a.skill.localeCompare(b.skill));
}

/** True when the two lists describe the same set of skills at the same levels. */
export function sameSpecialisations(a: Specialisation[], b: Specialisation[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const key = (list: Specialisation[]) =>
    [...list]
      .sort((x, y) => x.skill.localeCompare(y.skill))
      .map((s) => `${s.skill}:${s.level}`)
      .join('|');
  return key(a) === key(b);
}

interface PersonEditorProps {
  /** The person being edited, or null to add a new one. */
  person: Person | null;
  skills: SkillInfo[];
  /** Everyone, for the manager dropdown. */
  people: Person[];
  onSaved: (saved: Person) => void;
  onCancel: () => void;
}

export default function PersonEditor({
  person,
  skills,
  people,
  onSaved,
  onCancel,
}: PersonEditorProps) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const existing = person?.specialisations ?? [];

  const {
    register,
    handleSubmit,
    watch,
    formState: { dirtyFields, isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      email: person?.email ?? '',
      name: person?.name ?? '',
      manager_email: person?.manager_email ?? '',
      active: person?.active ?? true,
      // Every skill gets a key, so an untouched skill is an explicit 'none' rather
      // than an absent field that would read back as undefined.
      levels: Object.fromEntries(
        skills.map((s) => [s.skill, existing.find((e) => e.skill === s.skill)?.level ?? 'none'])
      ) as Record<string, Choice>,
    },
  });

  const levels = watch('levels');

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    const specialisations = buildSpecialisations(values.levels);

    try {
      if (!person) {
        const created = await createPerson({
          email: values.email.trim().toLowerCase(),
          name: values.name.trim(),
          manager_email: values.manager_email.trim().toLowerCase() || null,
          active: values.active,
          specialisations,
        });
        onSaved(created);
        return;
      }

      const patch: PersonPatch = {};
      if (dirtyFields.name) {
        patch.name = values.name.trim();
      }
      if (dirtyFields.manager_email) {
        patch.manager_email = values.manager_email.trim().toLowerCase() || null;
      }
      if (dirtyFields.active) {
        patch.active = values.active;
      }
      // Compared by value, not by dirtyFields - see the header.
      if (!sameSpecialisations(specialisations, existing)) {
        patch.specialisations = specialisations;
      }
      if (Object.keys(patch).length === 0) {
        onCancel();
        return;
      }
      onSaved(await patchPerson(person.email, patch));
    } catch (err) {
      setError(describeError(err));
    }
  });

  const onDeactivate = async () => {
    if (!person) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      onSaved(await deactivatePerson(person.email));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const disabled = isSubmitting || busy;

  return (
    <Form onSubmit={onSubmit}>
      <Label>
        Email
        <Input
          type="email"
          {...register('email', {
            required: 'An email address is the roster key.',
            // Deliberately loose. The backend is the authority on what it accepts;
            // a stricter pattern here would refuse addresses the API is happy with.
            pattern: { value: /.+@.+\..+/, message: 'That does not look like an address.' },
          })}
          disabled={person !== null}
          aria-invalid={Boolean(errors.email)}
          placeholder="name@qwealth.com"
        />
        <Hint>
          {person
            ? 'The roster key. Fixed once created.'
            : 'Adding somebody here does not create a login.'}
        </Hint>
      </Label>

      <Label>
        Name
        <Input
          {...register('name', { required: 'A name is needed.', maxLength: 120 })}
          aria-invalid={Boolean(errors.name)}
          placeholder="Full name"
        />
      </Label>

      <Label>
        Manager
        <Select {...register('manager_email')}>
          <option value="">Not recorded</option>
          {people
            .filter((p) => p.email !== person?.email)
            .map((p) => (
              <option key={p.email} value={p.email}>
                {p.name}
                {p.active ? '' : ' (inactive)'}
              </option>
            ))}
        </Select>
      </Label>

      <Skills>
        <SkillsLegend>Specialisations</SkillsLegend>
        <SkillGrid>
          {skills.map((skill) => (
            <SkillRow key={skill.skill}>
              <SkillName title={skill.description}>{skill.label}</SkillName>
              <Segments role="group" aria-label={skill.label}>
                {CHOICES.map((choice) => {
                  const on = (levels?.[skill.skill] ?? 'none') === choice.value;
                  return (
                    <Segment
                      key={choice.value}
                      $on={on}
                      $level={choice.value}
                      title={`${skill.label}: ${choice.hint}`}
                    >
                      <VisuallyHidden as="span">{skill.label} — </VisuallyHidden>
                      {choice.label}
                      <VisuallyHidden
                        as="input"
                        type="radio"
                        value={choice.value}
                        {...register(`levels.${skill.skill}` as const)}
                      />
                    </Segment>
                  );
                })}
              </Segments>
            </SkillRow>
          ))}
        </SkillGrid>
        <FullRow>
          <Hint>
            &ldquo;Ask them&rdquo; is the obvious person for the job; &ldquo;could cover&rdquo; is
            who to fall back on. There is no numeric scale on purpose.
          </Hint>
        </FullRow>
      </Skills>

      <Actions>
        <CheckLabel>
          <input type="checkbox" {...register('active')} />
          On the active roster
        </CheckLabel>
        <Spacer />
        {errors.email ? <ErrorText>{errors.email.message}</ErrorText> : null}
        {errors.name ? <ErrorText>{errors.name.message}</ErrorText> : null}
        {error ? <ErrorText role="alert">{error}</ErrorText> : null}
        {person && person.active ? (
          <SecondaryButton type="button" onClick={() => void onDeactivate()} disabled={disabled}>
            Deactivate
          </SecondaryButton>
        ) : null}
        <SecondaryButton type="button" onClick={onCancel} disabled={disabled}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={disabled}>
          {isSubmitting ? 'Saving…' : person ? 'Save person' : 'Add to roster'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
}
